import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Search, Clock, Brain, TrendingUp, Filter, BarChart3, Shield, Target, Users, RefreshCcw, FileText, CheckCircle2, Lock, Award, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const ease = [0.16, 1, 0.3, 1];
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6, ease, delay },
});
const stagger = (i) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-30px" },
  transition: { duration: 0.55, ease, delay: i * 0.09 },
});

const PROBLEMS = [
  { icon: Search, title: "Too Much Noise, Not Enough Signal", desc: "Job boards are flooded with duplicates, outdated posts, and irrelevant listings. Finding real opportunities takes hours every week." },
  { icon: Clock, title: "Applications Eat Time You Don't Have", desc: "Filling out forms, tailoring materials, and tracking submissions can take 15–20 hours a week — time better spent on preparation." },
  { icon: Brain, title: "While You Apply, Others Prepare", desc: "Candidates who win roles are deep into system design and mock interviews — not buried in job portals." },
  { icon: TrendingUp, title: "The Market Moves Fast", desc: "ML, AI, and security roles shift constantly. Staying current while actively job hunting is mentally exhausting." },
];

export default function FeaturesSection() {
  return (
    <>
      {/* ── PROBLEM ── */}
      <section className="py-28 md:py-36 bg-white" data-testid="problem-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp()} className="max-w-2xl mb-16">
            <p className="section-label-orange mb-1">The Problem</p>
            <motion.div className="h-0.5 w-8 bg-gradient-to-r from-brand to-brand/20 rounded-full mb-4" initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ duration: 0.5, ease, delay: 0.15 }} style={{ transformOrigin: "left" }} />
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-ink leading-snug mb-5">Job Search Shouldn't Be a Full-Time Job</h2>
            <p className="text-lg text-ink-secondary leading-relaxed">The modern job market is fragmented, noisy, and time-consuming. Candidates spend more energy applying than preparing.</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROBLEMS.map((p, i) => (
              <motion.div key={p.title} {...stagger(i)} className="bg-surface-raised rounded-3xl p-8 hover:bg-white hover:shadow-[0_8px_30px_rgba(255,82,0,0.10)] hover:-translate-y-1 transition-all duration-400 group">
                <motion.div whileHover={{ scale: 1.18, rotate: -8 }} transition={{ type: "spring", stiffness: 380, damping: 14 }} className="w-11 h-11 rounded-2xl bg-white flex items-center justify-center mb-5 shadow-sm group-hover:bg-brand-light transition-colors">
                  <p.icon size={20} className="text-ink-secondary group-hover:text-brand transition-colors" />
                </motion.div>
                <h3 className="text-ink font-medium text-base mb-2 leading-snug">{p.title}</h3>
                <p className="text-ink-secondary text-sm leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SOLUTION ── */}
      <section className="py-28 md:py-36 bg-surface-raised" data-testid="solution-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeUp()}>
              <p className="section-label-orange mb-1">The Solution</p>
              <motion.div className="h-0.5 w-8 bg-gradient-to-r from-brand to-brand/20 rounded-full mb-5" initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ duration: 0.5, ease, delay: 0.15 }} style={{ transformOrigin: "left" }} />
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-ink leading-snug mb-6">We Handle Applications.<br /><span className="gradient-text-orange">You Handle Your Career.</span></h2>
              <p className="text-lg text-ink-secondary leading-relaxed mb-8">ApplyStrat manages the entire process on your behalf — strategically, through official channels, aligned with your exact goals. You focus on what actually gets you hired.</p>
              <Button asChild size="lg" className="shadow-sm hover:scale-[1.02] hover:shadow-[0_8px_32px_rgba(255,82,0,0.40)]">
                <Link to="/apply" data-testid="solution-apply-button">Get Started <ArrowRight size={16} /></Link>
              </Button>
            </motion.div>
            <motion.div {...fadeUp(0.15)} className="space-y-3">
              {[
                { icon: Shield, text: "Applications submitted through official company portals only — no shortcuts, no spam tools." },
                { icon: Target, text: "Strategically applied based on your background, goals, and target companies." },
                { icon: FileText, text: "Resume and LinkedIn optimization included to reduce filter-outs before recruiter review." },
                { icon: RefreshCcw, text: "Weekly feedback loop to continuously refine strategy based on market signals." },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-5 bg-white rounded-2xl shadow-sm border border-black/5 hover:shadow-[0_4px_20px_rgba(255,82,0,0.10)] transition-all duration-300 group">
                  <motion.div whileHover={{ scale: 1.2, rotate: 10 }} transition={{ type: "spring", stiffness: 380, damping: 14 }} className="w-9 h-9 rounded-xl bg-brand-light flex items-center justify-center flex-shrink-0 mt-0.5">
                    <item.icon size={17} className="text-brand" />
                  </motion.div>
                  <p className="text-ink-secondary text-sm leading-relaxed">{item.text}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── AI SECTION ── */}
      <section className="py-28 md:py-36 bg-surface-raised" data-testid="ai-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeUp()}>
              <p className="section-label-orange mb-3">Modern Tools</p>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-ink mb-6">AI-Enhanced,<br /><span className="gradient-text-orange">Human-Led Execution</span></h2>
              <p className="text-lg text-ink-secondary leading-relaxed mb-4">We use modern AI tools and workflow automation to support faster research, cleaner job filtering, better market tracking, and more consistent execution.</p>
              <p className="text-ink-secondary text-sm leading-relaxed">Every strategy is grounded in validated data, human review, and your goals. AI improves our decisions — it doesn't replace judgment.</p>
            </motion.div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: TrendingUp, title: "Market Trend Monitoring", desc: "Continuous tracking of hiring signals and emerging role patterns." },
                { icon: Filter, title: "Role Filtering", desc: "Smart filtering to match your profile with high-signal openings." },
                { icon: BarChart3, title: "Validation Workflow", desc: "Applications validated against preferences before submission." },
                { icon: Users, title: "Human-Led Review", desc: "All final decisions reviewed by our team. AI supports, humans lead." },
              ].map((item, i) => (
                <motion.div key={item.title} {...stagger(i)} className="bg-white rounded-2xl p-5 border border-black/5 shadow-sm hover:shadow-[0_4px_20px_rgba(255,82,0,0.10)] hover:-translate-y-0.5 transition-all duration-300 group">
                  <motion.div whileHover={{ scale: 1.2, rotate: 10 }} transition={{ type: "spring", stiffness: 380, damping: 14 }} className="w-9 h-9 rounded-xl bg-brand-light flex items-center justify-center mb-4">
                    <item.icon size={17} className="text-brand" />
                  </motion.div>
                  <h4 className="text-ink font-medium text-sm mb-1.5">{item.title}</h4>
                  <p className="text-ink-secondary text-xs leading-relaxed">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── PROFILE OPTIMIZATION ── */}
      <section className="py-28 md:py-36 bg-white" data-testid="profile-optimization-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeUp()} className="space-y-3">
              {[
                { title: "Resume Optimization", desc: "Restructured to pass ATS filters and impress hiring managers." },
                { title: "LinkedIn & Profile Improvements", desc: "Headline, keyword, and positioning improvements for recruiter visibility." },
                { title: "Cover Letter Support", desc: "Tailored templates to help you stand out in competitive pipelines." },
                { title: "Job Tracking Spreadsheet", desc: "A clean, organized sheet tracking every application in progress." },
                { title: "Ongoing Strategy Refinement", desc: "Weekly adjustments based on response rates and market signals." },
              ].map((item, i) => (
                <motion.div key={item.title} {...stagger(i)} className="flex items-start gap-4 p-5 bg-surface-raised rounded-2xl hover:bg-white hover:shadow-sm transition-all duration-300">
                  <CheckCircle2 size={18} className="text-brand flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-ink font-medium text-sm mb-0.5">{item.title}</h4>
                    <p className="text-ink-secondary text-xs leading-relaxed">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
            <motion.div {...fadeUp(0.15)}>
              <p className="section-label-orange mb-3">What's Included</p>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-ink mb-6">More Than Just<br /><span className="gradient-text-orange">Submitting Applications</span></h2>
              <p className="text-lg text-ink-secondary leading-relaxed">Every plan includes profile optimization. We set you up to be perceived at your best before the first application leaves our queue.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── HONEST EXPECTATIONS ── */}
      <section className="py-28 md:py-36 bg-surface-raised" data-testid="honest-expectations-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp()} className="text-center max-w-2xl mx-auto mb-14">
            <p className="section-label-orange mb-3">Honesty First</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-ink mb-5">A Realistic Process for Serious Candidates</h2>
            <p className="text-lg text-ink-secondary">We respect you enough to be honest. Here's what we can and cannot promise.</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { icon: Clock, color: "text-amber-500", bg: "bg-amber-50", title: "Timeline Reality", desc: "Some candidates begin receiving calls within 6–8 weeks of actively entering the market. Every search is different. We do not promise specific timelines." },
              { icon: XCircle, color: "text-red-400", bg: "bg-red-50", title: "No Guarantees", desc: "We don't guarantee job offers, interview calls, or outcomes. Our role is to improve execution quality and save your time." },
              { icon: CheckCircle2, color: "text-green-500", bg: "bg-green-50", title: "Strategy Guarantee", desc: "Dissatisfied with our strategy? Request a full refund. Guaranteed based on strategy quality, not hiring results." },
            ].map((item, i) => (
              <motion.div key={item.title} {...stagger(i)} className="bg-white rounded-3xl p-8 border border-black/5 shadow-sm hover:shadow-[0_8px_30px_rgba(255,82,0,0.10)] hover:-translate-y-1 transition-all duration-400">
                <motion.div whileHover={{ scale: 1.18, rotate: 8 }} transition={{ type: "spring", stiffness: 380, damping: 14 }} className={`w-11 h-11 rounded-2xl ${item.bg} flex items-center justify-center mb-5`}>
                  <item.icon size={20} className={item.color} />
                </motion.div>
                <h3 className="text-ink font-medium text-base mb-3">{item.title}</h3>
                <p className="text-ink-secondary text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
